import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { rejectProduct } from '../services/po.stock.service';
import { HttpStatus } from 'pages/Core';
import notistack from 'pages/Core/Notistack';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

interface RejectProductProp {
    reloadDataSource?: () => void;
}

export interface RejectProductRef {
    open: (id: number, productName: string) => void;
    close: () => void;
}

interface FormValues {
    rejectReason: string;
    id?: number;
}

const RejectProduct = forwardRef<RejectProductRef, RejectProductProp>(
    ({ reloadDataSource }, ref) => {
        const navigate = useNavigate();

        const [isOpen, setIsOpen] = useState(false);
        const [selectedId, setSelectedId] = useState<number | null>(null);
        const [loading, setLoading] = useState(false);
        const [productDetails, setProductDetails] = useState<any>(null);

        const {
            register,
            handleSubmit,
            reset,
            setValue,
            formState: { errors }
        } = useForm<FormValues>();

        useImperativeHandle(ref, () => ({
            open: (id: number, productName: string) => {
                setSelectedId(id);
                setProductDetails(productName)
                setIsOpen(true);
            },
            close: () => {
                setIsOpen(false);
                setSelectedId(null);
                setProductDetails(null);
                reset();
            }
        }));

        const textPattern = {
            value: /^[A-Za-z0-9\s-]*$/,
            message: 'Please enter only letters, numbers, spaces, and hyphens'
        };

        const onSubmit = async (data: FormValues) => {
            if (!selectedId) return;

            try {
                setLoading(true);
                const payload = {
                    id: selectedId,
                    rejectReason: data.rejectReason.trim(),
                };

                const response = await rejectProduct(payload);
                if (response.status === HttpStatus.Success) {
                    setIsOpen(false);
                    reset();
                    setProductDetails(null);
                    reloadDataSource?.();
                    notistack.notifyFromApi(response);
                    navigate('/po-inward');
                } else {
                    notistack.notifyFromApi(response);
                }
            } catch (error: any) {
                notistack.error(error.message || 'Failed to update product');
            } finally {
                setLoading(false);
            }
        };

        const handleClose = () => {
            setIsOpen(false);
            setSelectedId(null);
            setProductDetails(null);
            reset();
        };

        return (
            <Modal
                show={isOpen}
                onHide={handleClose}
                backdrop="static"
                size="lg"
                dialogClassName="modal-dialog-centered"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Reject Product</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                {productDetails && (
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <div className="alert alert-info">
                                                <strong>Product:</strong> {productDetails}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="row">
                                    <div className="col-md-12 mb-2">
                                        <label className="form-label">
                                            Reject Reason <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            rows={4}
                                            className={`form-control ${errors.rejectReason ? 'is-invalid' : ''}`}
                                            {...register('rejectReason', {
                                                required: 'Reject reason is required',
                                                pattern: textPattern,
                                                minLength: {
                                                    value: 5,
                                                    message: 'Reject reason must be at least 5 characters'
                                                },
                                                maxLength: {
                                                    value: 500,
                                                    message: 'Reject reason cannot exceed 500 characters'
                                                }
                                            })}
                                            placeholder='Enter detailed reject reason...'
                                        />
                                        {errors.rejectReason && (
                                            <small className="text-danger">{errors.rejectReason.message}</small>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type="button"
                            className="btn add-btn me-2"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn submit-btn ms-2"
                            disabled={loading}
                        >
                            {loading ? 'Rejecting...' : 'Reject'}
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>
        );
    }
);

export default RejectProduct;